import React from "react";
import { useSelector } from "react-redux";
import {
  Autocomplete,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { Box, Grid } from "@mui/system";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import NegativeBalanceTable from "./NegativeBalanceTable";
import {
  selectBizBeAwareNegativeBalanceLoadedRequestKey,
  selectBizBeAwareNegativeBalanceLoading,
  selectBizBeAwareNegativeBalanceOutput,
} from "../../../../../../../features/bizDevelopmentDashboardSlice";
import { buildBeAwareRequestKey } from "../../../../../../../logic/BizDevelopment/Dashboard/BizDevelopmentDashboardEndPointLogic";
import { useBizDevelopmentDashboard } from "../../../../../../../pages/BizDevelopment/Dashboard/hooks/useBizDevelopmentDashboard";
import { exportBeAwareTableToCSV } from "../../beAwareCsvHelpers";
import type { BeAwareTableRef } from "../FraudPlayers/FraudPlayers";

const tableHeadings = {
  brand_name: "Brand Name",
  brand_id: "Brand ID",
  total_income: "Total Income",
  calculated_total_income: "Calculated Total Income",
  cpa_income: "CPA Income",
  rs_income: "RS Income",
  last_month_balance: "Last Month's End Balance",
  brand_policy_plan_name: "Brand Policy Plan Name",
  qftd: "QFTD",
  ftd: "FTD",
  regs: "Regs",
  click_out: "Click Outs",
};

// Keep raw values; empty string when missing so the table shows "N/A".
const raw = (value: any) =>
  value === null || value === undefined ? "" : String(value);

// Round monetary values to 2 decimals; empty string when missing/non-numeric.
const money = (value: any) => {
  if (value === null || value === undefined || value === "") return "";
  const n = Number(value);
  return Number.isNaN(n) ? String(value) : String(Math.round(n * 100) / 100);
};

// Monetary columns shown with a "$" icon in the table; prefixed with "$" in CSV.
const MONEY_KEYS = new Set([
  "total_income",
  "calculated_total_income",
  "cpa_income",
  "rs_income",
  "last_month_balance",
]);

// CSV cell resolver: prefix monetary columns with "$" to match the table.
const getNegativeBalanceCellValue = (key: string, row: any) => {
  const value = row[key] ?? "";
  return MONEY_KEYS.has(key) && value !== "" ? `$${value}` : value;
};

const NegativeBalance = React.forwardRef<BeAwareTableRef>((_, ref) => {
  const isLoading = useSelector(selectBizBeAwareNegativeBalanceLoading);
  const output = useSelector(selectBizBeAwareNegativeBalanceOutput);
  const loadedRequestKey = useSelector(
    selectBizBeAwareNegativeBalanceLoadedRequestKey,
  );
  const { fetchBeAwareNegativeBalanceData, getSitesFromParams } =
    useBizDevelopmentDashboard();

  React.useEffect(() => {
    const sites = getSitesFromParams();
    if (sites.length === 0) return;
    if (loadedRequestKey === buildBeAwareRequestKey(sites)) return;
    fetchBeAwareNegativeBalanceData(sites);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useImperativeHandle(ref, () => ({
    refresh: () => {
      fetchBeAwareNegativeBalanceData(getSitesFromParams());
    },
  }));

  const rows = React.useMemo(
    () =>
      (output ?? []).map((item: Record<string, any>) => ({
        brand_name: raw(item.brand_names),
        brand_id: raw(item.brand_id),
        total_income: money(item.totalincome_dollar),
        calculated_total_income: money(item.calculate_total_income),
        cpa_income: money(item.cpa_dollar),
        rs_income: money(item.revshare_dollar),
        last_month_balance: money(item.balance_end_last_month),
        brand_policy_plan_name: raw(item.brand_policy_plan_name),
        qftd: raw(item.qftd),
        ftd: raw(item.ftd),
        regs: raw(item.reg),
        click_out: raw(item.clicks),
      })),
    [output],
  );

  const [selectedBrand, setSelectedBrand] = React.useState<string | null>(null);

  // Unique, non-empty brand names used as the "Brand Name" search options.
  const brandOptions = React.useMemo(
    () =>
      (
        Array.from(
          new Set(
            rows
              .map((row: Record<string, any>) => row.brand_name)
              .filter((name: string) => name && name.trim() !== ""),
          ),
        ) as string[]
      ).sort(),
    [rows],
  );

  React.useEffect(() => {
    if (selectedBrand && !brandOptions.includes(selectedBrand)) {
      setSelectedBrand(null);
    }
  }, [brandOptions, selectedBrand]);

  const filteredRows = React.useMemo(
    () =>
      selectedBrand
        ? rows.filter(
            (row: Record<string, any>) => row.brand_name === selectedBrand,
          )
        : rows,
    [rows, selectedBrand],
  );

  const handleDownload = () => {
    exportBeAwareTableToCSV({
      tableHeadings,
      rows: filteredRows,
      filename: "negative_balance",
      getCellValue: getNegativeBalanceCellValue,
    });
  };

  return (
    <>
      <Box sx={{ width: "100%" }}>
        <Grid container spacing={2} alignItems="center" px={2} pb={2}>
          <Grid size={6}>
            <Typography fontSize={"18px"} mr={2}>
              Negative Balance in MTD
            </Typography>
          </Grid>
          <Grid size={6}>
            <Box
              display={"flex"}
              justifyContent={"flex-end"}
              alignItems={"center"}
            >
              <Autocomplete
                size="small"
                options={brandOptions}
                value={selectedBrand}
                onChange={(_, newValue) => setSelectedBrand(newValue)}
                disabled={isLoading}
                sx={{ width: 260 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Brand Name"
                    color="secondary"
                    slotProps={{ inputLabel: { shrink: true } }}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" sx={{ ml: 0.5 }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
              <IconButton
                aria-label="download"
                color="secondary"
                sx={{ ml: 2 }}
                disabled={isLoading || filteredRows.length === 0}
                onClick={handleDownload}
                data-tracker="857"
              >
                <DownloadIcon />
              </IconButton>
            </Box>
          </Grid>
        </Grid>
      </Box>
      <NegativeBalanceTable
        tableHeadings={tableHeadings}
        rows={filteredRows}
        isLoading={isLoading}
        filterKey={selectedBrand ?? ""}
      />
    </>
  );
});

export default NegativeBalance;
